import "firebase/auth";

import * as firebase from "firebase/app";
import { Module } from "vuex";

import { PatientsApi } from "@/api/patients";
import { PractitionersAPI } from "@/api/practitioners";
import { RoomInviteApi, RoomInviteCredentials } from "@/api/roomInvite";
import { RootState } from "@/types";
import { RolesEnum } from "@/types/Roles.enum";

export type Role =
  | RolesEnum.Patient
  | RolesEnum.Practitioner
  | RolesEnum.EncounterParticipant
  | RolesEnum.Owner
  | RolesEnum.Registrar;

const userRoles = [
  RolesEnum.Patient,
  RolesEnum.Practitioner,
  RolesEnum.EncounterParticipant,
  RolesEnum.Owner,
  RolesEnum.Registrar,
];

export interface AuthState {
  email: string | null;
  role: Role | null;
  token: string | null;
  uid: string | null;
  isLoggedIn: boolean;
  displayName: string | null;
  photoURL: string | null;
  timeZone: string;
  customToken: string | null;
  messageSend: boolean;
}

export const auth: Module<AuthState, RootState> = {
  state: {
    email: null,
    role: null,
    token: null,
    uid: null,
    messageSend: false,
    isLoggedIn: false,
    displayName: null,
    photoURL: null,
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    customToken: null,
  },
  mutations: {
    setToken(state, token: string) {
      state.token = token;
    },
    setRole(state, role: Role) {
      state.role = role;
    },
    setEmail(state, email: string) {
      state.email = email;
    },
    setUid(state, uid: string) {
      state.uid = uid;
    },
    setTimeZone(state, timeZone: string) {
      state.timeZone = timeZone;
    },
    setCustomToken(state, customToken: string) {
      state.customToken = customToken;
    },
    setDisplayName(state, displayName: string) {
      state.displayName = displayName;
    },
    setPhotoURL(state, photoURL: string) {
      state.photoURL = photoURL;
    },
    setMessageSended(state, payload: boolean) {
      state.messageSend = payload;
    },
    setIsLoggedIn(state, payload: boolean) {
      state.isLoggedIn = payload;
      if (!payload) {
        state.uid = null;
        state.token = null;
        state.role = null;
        state.email = null;
      }
    },
  },
  actions: {
    async logOut({ commit }) {
      await firebase.auth().signOut();
      commit("setIsLoggedIn", false);
    },
    async submitLogin({ dispatch, commit }, data) {
      const { email, password, rememberMe } = data;
      if (!rememberMe) {
        await firebase.auth().setPersistence(firebase.auth.Auth.Persistence.SESSION);
      } else {
        await firebase.auth().setPersistence(firebase.auth.Auth.Persistence.LOCAL);
      }
      await firebase.auth().signInWithEmailAndPassword(email.trim(), password.trim());
      await dispatch("actualizeUserInfo");
      await dispatch("updateTimeZone");
    },
    async signInWithCustomToken({ dispatch, state }) {
      await firebase.auth().setPersistence(firebase.auth.Auth.Persistence.SESSION);
      if (state.customToken) {
        await firebase.auth().signInWithCustomToken(state.customToken);
        await dispatch("actualizeUserInfo");
      } else {
        throw Error("No Custom token to authorize");
      }
    },
    async getCustomTokenRoom({ commit }, roomInviteCredentials: RoomInviteCredentials) {
      const token = await RoomInviteApi.getToken(roomInviteCredentials);
      if (token) {
        commit("setCustomToken", token);
      }
    },
    async refreshToken({ commit }) {
      const idTokenResult = await firebase.auth().currentUser?.getIdTokenResult(true);
      if (idTokenResult) {
        commit("setToken", idTokenResult.token);
      }
    },
    async resetPassword({ commit }, email: string) {
      await firebase
        .auth()
        .sendPasswordResetEmail(email)
        .then(() => {
          commit("setMessageSended", true);
        })
        .catch((error) => {
          commit("setMessageSended", error);
        });
    },
    async updateTimeZone({ commit, state }) {
      if (!state.uid || !state.role) {
        return;
      }
      const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      try {
        if (state.role === RolesEnum.Patient) {
          await PatientsApi.updateSettings(state.uid, { timeZone });
        } else if (state.role === RolesEnum.Practitioner) {
          await PractitionersAPI.practitionerUpdateSettings(state.uid, { timeZone });
        }
        commit("setTimeZone", timeZone);
      } catch (e) {
        console.error("Update timeZone failed", e.message || e);
      }
    },
    async actualizeUserInfo({ commit }) {
      try {
        const currentUser = await firebase.auth().currentUser;
        if (currentUser) {
          const idTokenResult = await currentUser.getIdTokenResult();
          if (idTokenResult) {
            const role = userRoles.find((r) => idTokenResult.claims[r]);
            if (role === RolesEnum.EncounterParticipant) {
              commit("setToken", idTokenResult.token);
              commit("setIsLoggedIn", false);
              commit("setRole", role);
            } else if (role) {
              commit("setIsLoggedIn", true);
              commit("setToken", idTokenResult.token);
              commit("setEmail", currentUser.email);
              commit("setRole", role);
              commit("setUid", currentUser.uid);
              commit("setDisplayName", currentUser.displayName);
              commit("setPhotoURL", currentUser.photoURL);
            } else {
              commit("setIsLoggedIn", false);
            }
          }
        } else {
          commit("setIsLoggedIn", false);
        }
      } catch (err) {
        commit("setIsLoggedIn", false);
        throw err;
      }
    },
  },
  getters: {
    role: (state) => state.role,
    userIsPatient: (state) => state.role === RolesEnum.Patient,
    userIsPractitioner: (state) => state.role === RolesEnum.Practitioner,
    email: (state) => state.email,
    token: (state) => state.token,
    uid: (state) => state.uid,
    isLoggedIn: (state) => state.isLoggedIn,
    displayName: (state) => state.displayName,
    photoURL: (state) => state.photoURL,
    messSended: (state) => state.messageSend,
  },
};
